import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Row, Col, Typography, Table, Tag, Statistic, Card, Tooltip } from 'antd';
import {
  LaptopOutlined,
  DesktopOutlined,
  WarningOutlined,
  FileProtectOutlined,
  ClockCircleOutlined,
  CloudServerOutlined,
  LinkOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { DashboardLayout } from '../components/layout';
import { StatCard, DataCard } from '../components/common';
import { useTenant } from '../context/TenantContext';
import { normalizeScalePadAsset, classifyWarranty } from '../utils/scalepadAssets';
import {
  useScalePadIdFromCipp,
  useScalePadHardwareAssets,
  useScalePadWarranty,
  useScalePadClientSummary,
} from '../hooks';

const { Title, Text } = Typography;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

// Table chrome comes from the root ConfigProvider (theme spec section 2.4): no page-local .ant-* overrides.
const StyledTable = styled(Table)``;

const WarrantyCard = styled(Card)`
  text-align: center;
`;

const MappingWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: var(--odin-status-awaiting-bg);
  border: 1px solid var(--odin-warning);
  border-radius: 8px;
  color: var(--odin-warning);
  margin-bottom: 24px;
`;

const RiskTag = styled(Tag)`
  text-transform: capitalize;
`;

const getAssetTypeIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'desktop': return <DesktopOutlined />;
    case 'laptop': return <LaptopOutlined />;
    case 'server': return <CloudServerOutlined />;
    case 'workstation': return <DesktopOutlined />;
    default: return <DesktopOutlined />;
  }
};

const getRiskLevel = (riskLevel) => {
  switch (riskLevel?.toLowerCase()) {
    case 'high':
      return { color: 'red', text: 'High Risk' };
    case 'medium':
      return { color: 'orange', text: 'Medium Risk' };
    case 'low':
      return { color: 'green', text: 'Low Risk' };
    default:
      return { color: 'default', text: 'Unknown' };
  }
};

const ScalePadPage = () => {
  const { selectedTenant } = useTenant();
  const cippTenantId = selectedTenant?.id;

  // Resolve CIPP tenant ID to ScalePad client ID
  const {
    scalepadId,
    isResolved,
    isLoading: isMappingLoading,
    customer,
  } = useScalePadIdFromCipp(cippTenantId, {
    enabled: !!cippTenantId,
  });

  const hasScalePadMapping = isResolved;

  // Fetch hardware assets with lifecycle data from ScalePad-MCP
  const { data: assetsData, isLoading: assetsLoading } = useScalePadHardwareAssets(
    scalepadId,
    {},
    { enabled: !!scalepadId }
  );

  // Fetch hardware summary
  const { data: summaryData, isLoading: summaryLoading } = useScalePadClientSummary(
    scalepadId,
    { enabled: !!scalepadId }
  );

  // Fetch warranty/lifecycle data
  const { data: lifecycleData, isLoading: lifecycleLoading } = useScalePadWarranty(
    scalepadId,
    { enabled: !!scalepadId }
  );

  // Parse and normalize hardware assets data
  const hardware = useMemo(() => {
    if (!assetsData) return [];

    // Handle various MCP response formats
    let parsed = null;
    if (typeof assetsData === 'string') {
      try {
        parsed = JSON.parse(assetsData);
      } catch {
        return [];
      }
    } else if (assetsData.content?.[0]?.text) {
      try {
        parsed = JSON.parse(assetsData.content[0].text);
      } catch {
        return [];
      }
    } else {
      parsed = assetsData;
    }

    // Extract assets array and flatten the tool's nested warranty/age/risk shape onto the
    // fields the columns and stats below read (see utils/scalepadAssets.js).
    const assets = parsed?.data?.assets || parsed?.assets || parsed?.data || [];
    return Array.isArray(assets) ? assets.map(normalizeScalePadAsset) : [];
  }, [assetsData]);

  // Calculate statistics from hardware data
  const stats = useMemo(() => {
    const totalAssets = hardware.length;

    // Count by type
    const desktops = hardware.filter(h => h.type?.toLowerCase() === 'desktop').length;
    const laptops = hardware.filter(h => h.type?.toLowerCase() === 'laptop').length;
    const servers = hardware.filter(h => h.type?.toLowerCase() === 'server').length;
    const workstations = hardware.filter(h => h.type?.toLowerCase() === 'workstation').length;

    // Count warranty statuses
    let warrantyExpiring = 0;
    let warrantyExpired = 0;
    let warrantyActive = 0;

    hardware.forEach(h => {
      const { status } = classifyWarranty(h.warrantyStatus, h.warrantyDaysRemaining);
      if (status === 'expiring' || status === 'warning') warrantyExpiring++;
      else if (status === 'expired') warrantyExpired++;
      else if (status === 'active') warrantyActive++;
    });

    // Count risk levels
    const highRisk = hardware.filter(h => h.riskLevel?.toLowerCase() === 'high').length;
    const mediumRisk = hardware.filter(h => h.riskLevel?.toLowerCase() === 'medium').length;
    const lowRisk = hardware.filter(h => h.riskLevel?.toLowerCase() === 'low').length;

    return {
      totalAssets,
      desktops,
      laptops,
      servers,
      workstations,
      warrantyExpiring,
      warrantyExpired,
      warrantyActive,
      highRisk,
      mediumRisk,
      lowRisk,
    };
  }, [hardware]);

  const hardwareLoading = isMappingLoading || assetsLoading || summaryLoading || lifecycleLoading;

  // Show mapping warning if no ScalePad mapping found
  const showMappingWarning = cippTenantId && !hasScalePadMapping && !isMappingLoading;

  // Asset table columns
  const assetColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => (
        <Tag icon={getAssetTypeIcon(type)}>
          {type || 'Unknown'}
        </Tag>
      ),
      filters: [
        { text: 'Desktop', value: 'desktop' },
        { text: 'Laptop', value: 'laptop' },
        { text: 'Server', value: 'server' },
        { text: 'Workstation', value: 'workstation' },
      ],
      onFilter: (value, record) => record.type?.toLowerCase() === value,
    },
    {
      title: 'Manufacturer',
      dataIndex: 'manufacturer',
      key: 'manufacturer',
      ellipsis: true,
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      ellipsis: true,
    },
    {
      title: 'Serial Number',
      dataIndex: 'serialNumber',
      key: 'serial',
      ellipsis: true,
    },
    {
      title: 'Warranty',
      key: 'warranty',
      width: 150,
      render: (_, record) => {
        const { color, text } = classifyWarranty(record.warrantyStatus, record.warrantyDaysRemaining);
        return (
          <Tag color={color}>
            {text}
            {record.warrantyExpiryDate && (
              <>
                <br />
                <small>{new Date(record.warrantyExpiryDate).toLocaleDateString()}</small>
              </>
            )}
          </Tag>
        );
      },
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Expiring Soon', value: 'expiring' },
        { text: 'Expired', value: 'expired' },
      ],
      onFilter: (value, record) => {
        const { status } = classifyWarranty(record.warrantyStatus, record.warrantyDaysRemaining);
        return status === value;
      },
    },
    {
      title: 'Risk',
      dataIndex: 'riskLevel',
      key: 'risk',
      width: 100,
      render: (riskLevel) => {
        const { color, text } = getRiskLevel(riskLevel);
        return <RiskTag color={color}>{text}</RiskTag>;
      },
      filters: [
        { text: 'High Risk', value: 'high' },
        { text: 'Medium Risk', value: 'medium' },
        { text: 'Low Risk', value: 'low' },
      ],
      onFilter: (value, record) => record.riskLevel?.toLowerCase() === value,
    },
    {
      title: 'Age',
      key: 'age',
      width: 80,
      render: (_, record) => {
        const purchaseDate = record.purchaseDate || record.purchase_date;
        if (!purchaseDate) return '--';
        const years = Math.floor((new Date() - new Date(purchaseDate)) / (1000 * 60 * 60 * 24 * 365));
        return (
          <Tooltip title={`Purchased: ${new Date(purchaseDate).toLocaleDateString()}`}>
            <span>{years}y</span>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        const dateA = new Date(a.purchaseDate || a.purchase_date || 0);
        const dateB = new Date(b.purchaseDate || b.purchase_date || 0);
        return dateA - dateB;
      },
    },
  ];

  return (
    <DashboardLayout>
      <PageHeader>
        <Title level={2} style={{ color: 'var(--odin-text-primary)', marginBottom: 8 }}>
          <LaptopOutlined style={{ marginRight: 12, color: 'var(--odin-accent)' }} />
          ScalePad Asset Management
        </Title>
        <Text style={{ color: 'var(--odin-text-secondary)' }}>
          {selectedTenant
            ? `${selectedTenant.displayName} - Hardware Inventory${customer ? ` (ScalePad: ${customer.displayName})` : ''}`
            : 'All Organizations - ScalePad Lifecycle'}
        </Text>
      </PageHeader>

      {/* Show mapping warning if applicable */}
      {showMappingWarning && (
        <MappingWarning>
          <LinkOutlined style={{ fontSize: 20 }} />
          <div>
            <Text strong style={{ color: 'var(--odin-warning)' }}>No ScalePad Mapping Found</Text>
            <br />
            <Text style={{ color: 'var(--odin-warning)' }}>
              This tenant doesn't have a ScalePad site mapping configured. Add the mapping in Odin-MCP's customer-mappings.json to view asset data.
            </Text>
          </div>
        </MappingWarning>
      )}

      {/* Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={DesktopOutlined}
            value={stats.totalAssets || null}
            label="Total Assets"
            loading={hardwareLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <WarrantyCard>
            <Statistic
              title="Warranty Expiring"
              value={stats.warrantyExpiring}
              prefix={<ClockCircleOutlined style={{ color: 'var(--odin-warning)' }} />}
              loading={hardwareLoading}
            />
          </WarrantyCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <WarrantyCard>
            <Statistic
              title="Warranty Expired"
              value={stats.warrantyExpired}
              prefix={<WarningOutlined style={{ color: 'var(--odin-error)' }} />}
              loading={hardwareLoading}
            />
          </WarrantyCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <WarrantyCard>
            <Statistic
              title="High Risk Devices"
              value={stats.highRisk}
              prefix={<ExclamationCircleOutlined style={{ color: 'var(--odin-error)' }} />}
              loading={hardwareLoading}
            />
          </WarrantyCard>
        </Col>
      </Row>

      {/* Asset Type Breakdown */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <DataCard title="Asset Type Breakdown" icon={DesktopOutlined} loading={hardwareLoading}>
            <Row gutter={[16, 16]} justify="center">
              <Col xs={8} sm={6} md={4}>
                <div style={{ textAlign: 'center' }}>
                  <DesktopOutlined style={{ fontSize: 32, color: 'var(--odin-accent)' }} />
                  <div style={{ marginTop: 8 }}>
                    <Text strong style={{ color: 'var(--odin-text-primary)', fontSize: 24 }}>{stats.desktops}</Text>
                    <br />
                    <Text type="secondary">Desktops</Text>
                  </div>
                </div>
              </Col>
              <Col xs={8} sm={6} md={4}>
                <div style={{ textAlign: 'center' }}>
                  <LaptopOutlined style={{ fontSize: 32, color: 'var(--odin-success)' }} />
                  <div style={{ marginTop: 8 }}>
                    <Text strong style={{ color: 'var(--odin-text-primary)', fontSize: 24 }}>{stats.laptops}</Text>
                    <br />
                    <Text type="secondary">Laptops</Text>
                  </div>
                </div>
              </Col>
              <Col xs={8} sm={6} md={4}>
                <div style={{ textAlign: 'center' }}>
                  <CloudServerOutlined style={{ fontSize: 32, color: 'var(--odin-warning)' }} />
                  <div style={{ marginTop: 8 }}>
                    <Text strong style={{ color: 'var(--odin-text-primary)', fontSize: 24 }}>{stats.servers}</Text>
                    <br />
                    <Text type="secondary">Servers</Text>
                  </div>
                </div>
              </Col>
              {stats.workstations > 0 && (
                <Col xs={8} sm={6} md={4}>
                  <div style={{ textAlign: 'center' }}>
                    <DesktopOutlined style={{ fontSize: 32, color: 'var(--odin-text-secondary)' }} />
                    <div style={{ marginTop: 8 }}>
                      <Text strong style={{ color: 'var(--odin-text-primary)', fontSize: 24 }}>{stats.workstations}</Text>
                      <br />
                      <Text type="secondary">Workstations</Text>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </DataCard>
        </Col>
      </Row>

      {/* Assets Table */}
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <DataCard
            title="Hardware Inventory"
            icon={LaptopOutlined}
            loading={hardwareLoading}
            empty={!hardware?.length && !showMappingWarning}
            emptyText={
              showMappingWarning
                ? 'Configure ScalePad mapping to view assets'
                : selectedTenant
                  ? 'No assets found for this tenant'
                  : 'Select an organization to view assets'
            }
          >
            <StyledTable
              dataSource={hardware}
              columns={assetColumns}
              rowKey={(record) => record.id || record.serialNumber || Math.random().toString()}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              size="small"
            />
          </DataCard>
        </Col>
      </Row>
    </DashboardLayout>
  );
};

export default ScalePadPage;
